import { useQuery } from "@tanstack/react-query";
import { reportService } from "../../services/reports";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { CheckSquare, Target, TrendingUp } from "lucide-react";

const MetricCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);

const TeamReport = ({ filters }) => {
  const { data } = useQuery({
    queryKey: ["team-report", filters],
    queryFn: () => reportService.getTeamPerformance(filters),
  });

  if (!data) return <div className="text-center py-8 text-gray-400">Loading...</div>;

  const tasksByUser = data.tasksByUser || [];
  const leadsByUser = data.leadsByUser || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <MetricCard icon={CheckSquare} label="Total Tasks" value={data.totalTasks || 0} color="bg-blue-500" />
        <MetricCard icon={Target} label="Completed" value={data.completedTasks || 0} color="bg-emerald-500" />
        <MetricCard icon={TrendingUp} label="Completion Rate" value={`${data.overallCompletionRate || 0}%`} color="bg-purple-500" />
      </div>

      {tasksByUser.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Tasks by Team Member</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={tasksByUser} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={100} />
              <Tooltip />
              <Legend />
              <Bar dataKey="completed" name="Completed" fill="#10b981" radius={[0, 4, 4, 0]} />
              <Bar dataKey="total" name="Total" fill="#3b82f6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {tasksByUser.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Individual Performance</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Role</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Tasks</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Completed</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">High Priority</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Completion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tasksByUser.map((member, i) => (
                  <tr key={i} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{member.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 capitalize">{member.role}</td>
                    <td className="px-4 py-3 text-sm text-center text-gray-700">{member.total}</td>
                    <td className="px-4 py-3 text-sm text-center text-emerald-600 font-medium">{member.completed}</td>
                    <td className="px-4 py-3 text-sm text-center text-amber-600 font-medium">{member.highPriority || 0}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${member.completionRate?.toFixed(0) || 0}%` }} />
                        </div>
                        <span className="text-xs font-medium text-gray-600 w-10">{member.completionRate?.toFixed(0) || 0}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {leadsByUser.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Lead Conversion by Member</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={leadsByUser}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="total" name="Assigned" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="converted" name="Converted" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default TeamReport;
